<template>
  <div>
    <!-- Hero -->
    <section class="hero hero-page gray-bg padding-small">
      <div class="container">
        <div class="row d-flex">
          <div class="col-lg-9 order-2 order-lg-1">
            <h1>
              {{ localize(props.design.names) }}
              {{ localize(props.style.names) }}
            </h1>
          </div>
          <div class="col-lg-3 text-right order-1 order-lg-2">
            <ul class="breadcrumb justify-content-lg-end">
              <li class="breadcrumb-item">
                <a href="/">{{ $t('Home') }}</a>
              </li>
              <li class="breadcrumb-item">
                <a>{{
                  props.design.priceClass === 'solid'
                    ? $t('Solid')
                    : $t('Repeat Print')
                }}</a>
              </li>
              <li class="breadcrumb-item active">
                {{ localize(props.design.names) }}
                {{ localize(props.style.names) }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Product detail -->
    <section class="product-details">
      <div class="container">
        <div class="row">
          <!-- Images -->
          <div class="product-images col-lg-6">
            <div class="ribbon-info text-uppercase">
              {{
                props.design.priceClass === 'solid'
                  ? $t('Solid')
                  : $t('Repeat Print')
              }}
            </div>

            <!-- Carousel -->
            <div data-slider-id="1" class="owl-carousel items-slider owl-drag">
              <template v-if="hasDesignImages">
                <div class="item">
                  <img
                    :src="designImageUrl('front')"
                    onerror="this.src = '/images/comingsoon.png';"
                    class="drawing"
                  />
                </div>
                <div class="item">
                  <img
                    :src="designImageUrl('back')"
                    onerror="this.src = '/images/comingsoon.png';"
                    class="drawing"
                  />
                </div>
              </template>
              <template v-else>
                <div class="item">
                  <img
                    :src="styleImageUrl('front')"
                    onerror="this.src = '/images/comingsoon.png';"
                    class="drawing"
                  />
                </div>
                <div class="item">
                  <img
                    :src="styleImageUrl('back')"
                    onerror="this.src = '/images/comingsoon.png';"
                    class="drawing"
                  />
                </div>
                <div class="item">
                  <template v-if="props.design.priceClass === 'solid'">
                    <img
                      class="img-fluid"
                      :src="`${props.blobDomain}static/colors/${props.solidColorId}.svg`"
                      onerror="this.src = '/images/comingsoon.png';"
                    />
                    <h5 class="mt-3 text-center">{{ $t('Solid') }}</h5>
                  </template>
                  <template v-else>
                    <img
                      :src="props.printIconImageUrl ?? ''"
                      onerror="this.src = '/images/comingsoon.png';"
                      alt=""
                      class="img-fluid"
                    />
                    <h5 class="mt-3 text-center">{{ $t('Print') }}</h5>
                  </template>
                </div>
              </template>
            </div>

            <!-- Thumbs -->
            <div
              data-slider-id="1"
              class="owl-thumbs d-flex align-items-center justify-content-center"
            >
              <template v-if="hasDesignImages">
                <button class="owl-thumb-item">
                  <img
                    :src="designImageUrl('front')"
                    onerror="this.src = '/images/comingsoon.png';"
                  />
                </button>
                <button class="owl-thumb-item">
                  <img
                    :src="designImageUrl('back')"
                    onerror="this.src = '/images/comingsoon.png';"
                  />
                </button>
              </template>
              <template v-else>
                <div class="owl-thumb-item">
                  <img
                    :src="styleImageUrl('front')"
                    onerror="this.src = '/images/comingsoon.png';"
                  />
                </div>
                <div class="owl-thumb-item">
                  <img
                    :src="styleImageUrl('back')"
                    onerror="this.src = '/images/comingsoon.png';"
                  />
                </div>
                <div class="owl-thumb-item">
                  <img
                    v-if="props.design.priceClass === 'solid'"
                    class="img-fluid"
                    :src="`${props.blobDomain}static/colors/${props.solidColorId}.svg`"
                    onerror="this.src = '/images/comingsoon.png';"
                  />
                  <img
                    v-else
                    :src="props.printIconImageUrl ?? ''"
                    onerror="this.src = '/images/comingsoon.png';"
                    alt=""
                    class="img-fluid"
                  />
                </div>
              </template>
            </div>

            <!-- Action buttons -->
            <div class="text-center">
              <!-- Customize This Design -->
              <form
                v-if="props.hasCustomizeButton"
                :method="props.customizeMethod"
                :action="props.customizeUrl"
              >
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span
                  v-if="props.customizeMethod === 'post'"
                  v-html="props.xsrfTokenInput"
                ></span>
                <input
                  v-else
                  type="hidden"
                  name="returnUrl"
                  :value="props.customizeReturnUrl"
                />
                <button
                  type="submit"
                  class="btn btn-template-outlined wide mb-3"
                >
                  <img
                    height="30"
                    :src="'/images/agonCustomize.svg'"
                    alt="Customize This Design"
                  />
                </button>
              </form>

              <!-- Email to sales rep -->
              <a
                :href="props.sendMessageUrl"
                class="btn btn-outline-success wide mb-3"
              >
                {{ $t('EmailDesignToSalesRep') }}
              </a>
            </div>

            <!-- Favorite button -->
            <div v-if="props.isAuthenticated" class="text-center mt-2">
              <FavoriteButton
                :is-favorite="isFavorite"
                item-type="noncustom"
                :item-id="catalogProductId"
                :xsrf-token="props.xsrfToken"
                @toggle="onFavoriteToggle"
              />
            </div>
          </div>

          <!-- Details / Add to cart -->
          <div class="details col-lg-6">
            <!-- Price -->
            <div
              class="d-flex align-items-center justify-content-between flex-column flex-sm-row"
            >
              <ul
                v-if="!isProductUnavailable"
                class="price list-inline no-margin"
              >
                <template v-if="data.product?.isSizePriceVariable">
                  <li class="list-inline-item current">
                    {{ asCurrency(data.product.minCustomerPrice.amount) }} –
                    {{ asCurrency(data.product.maxCustomerPrice.amount) }}
                    <small v-if="props.showDisplayCurrency">
                      {{
                        asDisplayCurrency(
                          data.product.minCustomerPrice.amount *
                            displayExchangeRate,
                        )
                      }}
                      –
                      {{
                        asDisplayCurrency(
                          data.product.maxCustomerPrice.amount *
                            displayExchangeRate,
                        )
                      }}<sup>1</sup>
                    </small>
                  </li>
                  <li
                    v-if="
                      data.product.retailPrice.amount !==
                      data.product.customerPrice.amount
                    "
                    class="list-inline-item original"
                  >
                    <s
                      >{{ asCurrency(data.product.minRetailPrice.amount) }} –
                      {{ asCurrency(data.product.maxRetailPrice.amount) }}</s
                    >
                    <small v-if="props.showDisplayCurrency">
                      <s
                        >{{
                          asDisplayCurrency(
                            data.product.minRetailPrice.amount *
                              displayExchangeRate,
                          )
                        }}
                        –
                        {{
                          asDisplayCurrency(
                            data.product.maxRetailPrice.amount *
                              displayExchangeRate,
                          )
                        }}</s
                      ><sup>1</sup>
                    </small>
                  </li>
                </template>
                <template v-else>
                  <li class="list-inline-item current">
                    {{ asCurrency(data.product?.customerPrice.amount ?? 0) }}
                    <small v-if="props.showDisplayCurrency">
                      {{
                        asDisplayCurrency(
                          (data.product?.customerPrice.amount ?? 0) *
                            displayExchangeRate,
                        )
                      }}<sup>1</sup>
                    </small>
                  </li>
                  <li
                    v-if="
                      data.product &&
                      data.product.retailPrice.amount !==
                        data.product.customerPrice.amount
                    "
                    class="list-inline-item original"
                  >
                    <s>{{ asCurrency(data.product.retailPrice.amount) }}</s>
                    <small v-if="props.showDisplayCurrency">
                      <s>{{
                        asDisplayCurrency(
                          data.product.retailPrice.amount * displayExchangeRate,
                        )
                      }}</s
                      ><sup>1</sup>
                    </small>
                  </li>
                </template>
              </ul>
            </div>

            <p>{{ $t('SelectFabricLiningAndQuantity') }}</p>

            <form id="cartForm" :action="props.cartAddUrl" method="post">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span v-html="props.xsrfTokenInput"></span>
              <input
                v-model="data.recaptchaToken"
                type="hidden"
                name="g-recaptcha-response"
              />

              <!-- Fabric -->
              <div class="d-flex align-items-center justify-content-around">
                <ul class="list-inline">
                  <li class="list-inline-item">
                    <h4>{{ $t('Fabric') }}</h4>
                  </li>
                  <li class="list-inline-item">
                    <select
                      v-model="data.fabricId"
                      :class="{ 'is-invalid': data.fabricId === 'unk' }"
                      class="form-control"
                      size="1"
                      @change="updateProduct()"
                    >
                      <option v-if="props.fabrics.length !== 1" value="unk">
                        {{ $t('Select fabric') }}
                      </option>
                      <option
                        v-for="fabric in props.fabrics"
                        :key="fabric.id"
                        :value="fabric.agonswimId"
                        :selected="props.fabrics.length === 1"
                      >
                        {{ localize(fabric.names) }}
                      </option>
                    </select>
                    <div class="invalid-feedback">
                      {{ $t('Please select a fabric.') }}
                    </div>
                  </li>
                </ul>
              </div>

              <!-- Lining -->
              <div class="d-flex align-items-center justify-content-around">
                <ul class="list-inline">
                  <li class="list-inline-item">
                    <h4>{{ $t('Lining') }}</h4>
                  </li>
                  <li class="list-inline-item form-group">
                    <select
                      v-model="data.liningId"
                      :class="{ 'is-invalid': data.liningId === 'unk' }"
                      class="form-control"
                      size="1"
                      @change="updateProduct()"
                    >
                      <option
                        v-if="props.liningOptions.length !== 1"
                        value="unk"
                      >
                        {{ $t('Select lining') }}
                      </option>
                      <option
                        v-for="lining in props.liningOptions"
                        :key="lining.oldId"
                        :value="lining.oldId"
                        :selected="props.liningOptions.length === 1"
                      >
                        {{ localize(lining.names) }}
                      </option>
                    </select>
                    <div class="invalid-feedback">
                      {{ $t('Please select a lining option.') }}
                    </div>
                  </li>
                </ul>
              </div>

              <!-- Quantity -->
              <div class="row">
                <div class="col-sm-12">
                  <h4
                    v-if="!(isProductUnavailable || props.isPrintWaterPoloCap)"
                    class="text-center"
                  >
                    {{ $t('Quantity') }}
                  </h4>
                  <div
                    v-if="!(isProductUnavailable || props.isPrintWaterPoloCap)"
                    class="row"
                  >
                    <div
                      v-for="psize in orderBy(
                        data.product?.sizes ?? [],
                        'size.order',
                      )"
                      :key="psize.size.id"
                      class="col-6 col-md-4"
                    >
                      <div class="form-group">
                        <label
                          :for="'size' + psize.size.id"
                          class="text-center"
                        >
                          {{ $t('Size') }} {{ psize.size.values['agon'] }}
                          <span v-if="data.product?.isSizePriceVariable">
                            – {{ asCurrency(psize.customerPrice.amount) }}
                          </span>
                        </label>
                        <select
                          v-if="
                            data.quantities[catalogProductId + psize.size.id]
                              ?.showDropdown
                          "
                          :id="
                            'quantities[' +
                            catalogProductId +
                            psize.size.id +
                            ']'
                          "
                          v-model.number="
                            data.quantities[catalogProductId + psize.size.id]
                              .qty
                          "
                          :name="
                            'quantities[' +
                            catalogProductId +
                            psize.size.id +
                            ']'
                          "
                          class="form-control"
                          size="1"
                          @change="updateQuantity()"
                        >
                          <option
                            v-for="option in qtyOptions"
                            :key="option.value"
                            :value="option.value"
                          >
                            {{ option.text }}
                          </option>
                        </select>
                        <input
                          v-if="
                            data.quantities[catalogProductId + psize.size.id]
                              ?.showDropdown === false
                          "
                          v-model.number="
                            data.quantities[catalogProductId + psize.size.id]
                              .qty
                          "
                          :id="
                            'quantities[' +
                            catalogProductId +
                            psize.size.id +
                            ']'
                          "
                          :name="
                            'quantities[' +
                            catalogProductId +
                            psize.size.id +
                            ']'
                          "
                          style="display: inline"
                          size="5"
                          type="number"
                          min="0"
                          step="1"
                          class="form-control"
                          @change="updateQuantity()"
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="isProductUnavailable || props.isPrintWaterPoloCap"
                    class="row"
                  >
                    <div class="col-md-12">
                      <h4 v-if="isProductUnavailable">
                        {{
                          $t(
                            'This product is not available. Please select another fabric or lining.',
                          )
                        }}
                      </h4>
                      <h4 v-if="props.isPrintWaterPoloCap">
                        {{ $t('PrintWaterPoloCapUnavailable') }}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Water polo personalizations -->
              <div
                v-if="props.isWaterPoloCap && !props.isPrintWaterPoloCap"
                class="row"
              >
                <div class="col-sm-12">
                  <WaterPoloPersonalizations
                    :quantities="data.quantities"
                    :catalog-product-id-length="catalogProductId.length"
                  />
                </div>
              </div>

              <!-- Add to cart -->
              <ul class="CTAs list-inline">
                <li v-if="!props.isPrintWaterPoloCap" class="list-inline-item">
                  <button
                    type="button"
                    class="btn btn-template wide form-control mb-3"
                    :class="{ 'is-invalid': isSubmitDisabled }"
                    :disabled="isSubmitDisabled"
                    @click="recaptcha"
                  >
                    <i class="icon-cart"></i> {{ $t('Add to cart') }}
                  </button>
                  <div class="invalid-feedback">
                    {{ $t('Please select at least one size.') }}
                  </div>
                </li>
              </ul>
            </form>
          </div>
        </div>
      </div>
    </section>

    <!-- Description tabs -->
    <section class="product-description no-padding">
      <div class="container">
        <ul role="tablist" class="nav nav-tabs">
          <li class="nav-item">
            <a
              data-toggle="tab"
              href="#style-detail"
              role="tab"
              class="nav-link active"
              >{{ $t('Style Details') }}</a
            >
          </li>
          <li class="nav-item">
            <a
              data-toggle="tab"
              href="#lining-detail"
              role="tab"
              class="nav-link"
              >{{ $t('Lining Details') }}</a
            >
          </li>
          <li class="nav-item">
            <a
              data-toggle="tab"
              href="#fabric-detail"
              role="tab"
              class="nav-link"
              >{{ $t('Fabric Details') }}</a
            >
          </li>
          <li class="nav-item">
            <a
              data-toggle="tab"
              href="#size-detail"
              role="tab"
              class="nav-link"
              >{{ $t('Size Details') }}</a
            >
          </li>
          <li class="nav-item">
            <a
              data-toggle="tab"
              href="#piece-detail"
              role="tab"
              class="nav-link"
              >{{ $t('Piece Details') }}</a
            >
          </li>
        </ul>
        <div class="tab-content">
          <div id="style-detail" role="tabpanel" class="tab-pane active">
            <h4>{{ localize(props.style.names) }}</h4>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <p v-html="localize(props.style.descriptions)"></p>
          </div>
          <div id="lining-detail" role="tabpanel" class="tab-pane">
            <div v-for="lining in props.liningOptions" :key="lining.oldId">
              <h4>{{ localize(lining.names) }}</h4>
              <p>{{ localize(lining.descriptions) }}</p>
            </div>
          </div>
          <div id="fabric-detail" role="tabpanel" class="tab-pane">
            <FabricSummary
              v-for="fabric in props.fabrics"
              :key="fabric.id"
              :blob-domain="props.blobDomain"
              :fabric="fabric"
              :culture="props.culture"
            />
          </div>
          <div id="size-detail" role="tabpanel" class="tab-pane">
            <div class="row">
              <div v-if="data.product?.product">
                <div
                  v-if="data.product.product.showSizeCalculator"
                  class="col-md-6 col-12"
                >
                  <SizeCalculator
                    :assembly="props.assembly"
                    :product="data.product.product"
                    :culture="props.culture"
                  />
                </div>
                <div v-else class="col-md-6 col-12">
                  <SizeDetail
                    :assembly="props.assembly"
                    :product="data.product.product"
                    :culture="props.culture"
                  />
                </div>
              </div>
            </div>
          </div>
          <div id="piece-detail" role="tabpanel" class="tab-pane">
            <PiecesDetail
              v-if="data.product?.product"
              :assembly="props.assembly"
              :product="data.product.product"
              :culture="props.culture"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, onBeforeMount } from 'vue';
import { useReCaptcha } from 'vue-recaptcha-v3';
import { orderBy } from 'lodash-es';
import { localize as genLocalize } from '../../common/localize';
import SizeCalculator from '../../components/SizeCalculator.vue';
import SizeDetail from '../../components/SizeDetail.vue';
import PiecesDetail from '../../components/PiecesDetail.vue';
import FabricSummary from '../../components/FabricSummary.vue';
import FavoriteButton from '../../components/FavoriteButton.vue';
import WaterPoloPersonalizations from './WaterPoloPersonalizations.vue';
import type { Design, Style } from '../../common/CatalogItem';
import type { Assembly } from '../../common/Assembly';
import type { PricedProduct, PricedProductSize } from '../../common/Product';
import type { Fabric, LiningOption } from '@agon/shared-utils';

interface Props {
  culture: string;
  currency: string;
  blobDomain: string;
  designId: string;
  styleId: string;
  fabricId: string;
  liningId: string;
  style: Style;
  assembly: Assembly;
  design: Design;
  fabrics: Fabric[];
  liningOptions: LiningOption[];
  products: PricedProduct[];
  showDisplayCurrency: boolean;
  displayCurrency: string;
  displayExchangeRate: string;
  xsrfTokenInput: string;
  xsrfToken: string;
  isAuthenticated: boolean;
  isPrintWaterPoloCap: boolean;
  isWaterPoloCap: boolean;
  hasCustomizeButton: boolean;
  customizeMethod: string;
  customizeUrl: string;
  customizeReturnUrl: string;
  sendMessageUrl: string;
  solidColorId: string | null;
  printIconImageUrl: string | null;
  favorites: string[];
  cartAddUrl: string;
}

const props = defineProps<Props>();

const qtyOptions = [
  { text: '0', value: '0' },
  { text: '1', value: '1' },
  { text: '2', value: '2' },
  { text: '3', value: '3' },
  { text: '4', value: '4' },
  { text: '5', value: '5' },
  { text: '6', value: '6' },
  { text: '7', value: '7' },
  { text: '8', value: '8' },
  { text: '9', value: '9' },
  { text: '10+', value: '10' },
];

interface Quantity {
  personalizations: unknown[];
  showDropdown: boolean;
  qty: number;
}

interface Data {
  quantities: Record<string, Quantity>;
  product?: PricedProduct;
  sizes: PricedProductSize[];
  liningId: string;
  fabricId: string;
  recaptchaToken: string;
  favorites: string[];
}

const data = reactive<Data>({
  quantities: {},
  sizes: [],
  liningId: props.liningId,
  fabricId: props.fabricId,
  recaptchaToken: 'none',
  favorites: props.favorites,
});

const { executeRecaptcha, recaptchaLoaded } = useReCaptcha()!;

const recaptcha = async (e: Event) => {
  e.preventDefault();
  await recaptchaLoaded();
  data.recaptchaToken = await executeRecaptcha('cartadd');
  setTimeout(() => {
    (document.forms as unknown as Record<string, HTMLFormElement>)[
      'cartForm'
    ]?.submit();
  }, 250);
};

const displayExchangeRate = computed(() => Number(props.displayExchangeRate));

const hasDesignImages = computed(() => props.design.imageUpdated != null);

const designImageUrl = (view: string) => props.design.images[view];

const styleImageUrl = (view: string) =>
  `${props.blobDomain}static/style/${props.style.agonswimId}/${props.style.assembly}-view-${view}.svg`;

const localize = (dict: Record<string, string>) =>
  genLocalize(props.culture, dict);

const updateQuantity = () => {
  for (const key in data.quantities) {
    data.quantities[key].personalizations = new Array(
      Number(data.quantities[key].qty),
    );
    data.quantities[key].showDropdown = data.quantities[key].qty < 10;
  }
};

const productId = computed(
  () => `${props.style.agonswimId}${data.liningId}${data.fabricId}`,
);

const catalogProductId = computed(() => {
  const prefix =
    props.design.assembly === 'water-polo-cap-8-cm-numbers'
      ? 'awcxxx' + (props.design.cpidPrefix ?? '').substring(6, 9)
      : (props.design.cpidPrefix ?? '');
  return prefix + productId.value;
});

const updateProduct = () => {
  if (productId.value.length === 7) {
    const foundProduct = props.products.find(
      (p) => p.product.id === productId.value,
    );
    if (!foundProduct) {
      data.product = undefined;
      data.quantities = {};
      return;
    }
    data.product = foundProduct;
    data.quantities = orderBy(foundProduct.product.sizes, 'order').reduce<
      Record<string, Quantity>
    >((result, value) => {
      result[catalogProductId.value + value.id] = {
        personalizations: [],
        qty: foundProduct.product.sizes.length === 1 ? 1 : 0,
        showDropdown: true,
      };
      return result;
    }, {});
  } else {
    data.product = undefined;
  }
};

const onFavoriteToggle = (itemId: string) => {
  data.favorites = data.favorites.includes(itemId)
    ? data.favorites.filter((f) => f !== itemId)
    : [...data.favorites, itemId];
};

const isSubmitDisabled = computed(() => {
  if (!data.quantities) return true;
  return Object.values(data.quantities).every((v) => v === null || v.qty === 0);
});

const isProductUnavailable = computed(() => {
  if (productId.value.length !== 7) return true;
  if (productId.value.includes('unk')) return true;
  if (!data.product) return true;
  return (
    (data.product.product == null || data.product.product.sizes.length === 0) &&
    productId.value.length === 7
  );
});

const isFavorite = computed(() =>
  data.favorites.includes(catalogProductId.value),
);

const asCurrency = (input: number) =>
  input.toLocaleString(props.culture, {
    style: 'currency',
    currency: props.currency,
  });

const asDisplayCurrency = (input: number) =>
  input.toLocaleString(props.culture, {
    style: 'currency',
    currency: props.displayCurrency,
  });

onBeforeMount(() => updateProduct());
</script>
